/**
 * @file msalBootstrap.ts
 * @description This file handles the asynchronous initialization of the MSAL (Microsoft Authentication Library) instance.
 * It ensures that the necessary authentication configuration is fetched from the server *before* the MSAL
 * PublicClientApplication is created. This prevents race conditions and ensures the library is ready
 * before the main application renders.
 */

import { PublicClientApplication, AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { msalConfig as getMsalConfig, initializeAuthSetup } from "../authConfig";

// A singleton instance of the MSAL PublicClientApplication. It is initialized by `initializeMsal`.
let msalInstance: PublicClientApplication | null = null;

/**
 * @function initializeMsal
 * @description Creates and initializes the MSAL PublicClientApplication instance. This function is designed to be
 * called once at the application's entry point (e.g., in `index.tsx`) before the React tree is rendered.
 * It handles the MSAL redirect promise and sets the active user account.
 * @returns {Promise<PublicClientApplication>} A promise that resolves with the fully initialized MSAL instance.
 */
export async function initializeMsal(): Promise<PublicClientApplication> {
    console.log("[msalBootstrap] Initializing MSAL...");

    // Step 1: Ensure the remote authentication configuration is fetched and ready.
    await initializeAuthSetup();

    // Step 2: Create the MSAL instance now that the configuration is guaranteed to be available.
    msalInstance = new PublicClientApplication(getMsalConfig());
    await msalInstance.initialize();

    let response: AuthenticationResult | null = null;
    try {
        response = await msalInstance.handleRedirectPromise();
    } catch (err) {
        console.error("[msalBootstrap] handleRedirectPromise() failed:", err);
    }

    // Step 3: Set the active account. Prioritize the account from the redirect response.
    if (response?.account) {
        console.log("[msalBootstrap] Redirect response received, setting active account.");
        msalInstance.setActiveAccount(response.account);
    } else {
        // If there's no redirect response, check for any cached accounts from previous sessions.
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            console.log("[msalBootstrap] Found cached accounts, setting first as active.");
            msalInstance.setActiveAccount(accounts[0]);
        } else {
            console.log("[msalBootstrap] No account found yet.");
        }
    }

    console.log("[msalBootstrap] MSAL initialization complete.");
    return msalInstance;
}

/**
 * @function getMsalInstance
 * @description A synchronous getter for the MSAL instance.
 * @throws {Error} If called before `initializeMsal` has completed.
 * @returns {PublicClientApplication} The singleton MSAL instance.
 */
export function getMsalInstance(): PublicClientApplication {
    if (!msalInstance) {
        throw new Error("MSAL instance not initialized. Call initializeMsal first.");
    }
    return msalInstance;
}
